import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAdminStats, listLatest } from "@/lib/admin";
import { coverFor } from "@/lib/defaultEventImages";
import Avatar from "@/components/Avatar";
import { Users, Calendar, Flag, UserPlus, Sparkles } from "lucide-react";

const STAT_DEFS = [
  { key: "users",        label: "Users",        icon: Users },
  { key: "events",       label: "Events",       icon: Calendar },
  { key: "reports",      label: "Open reports", icon: Flag,    accent: true },
  { key: "todaySignups", label: "New today",    icon: UserPlus },
  { key: "activeEvents", label: "Active",       icon: Sparkles },
];

function fmt(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; }
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let alive = true;
    Promise.all([getAdminStats(), listLatest("profiles", 6), listLatest("events", 6)])
      .then(([s, u, e]) => alive && (setStats(s), setUsers(u), setEvents(e)))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div className="flex flex-col gap-8">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {STAT_DEFS.map((s) => {
          const Icon = s.icon;
          const value = stats?.[s.key];
          return (
            <div
              key={s.key}
              data-testid={`stat-${s.key}`}
              className={`pop p-4 md:p-5 ${s.accent && value > 0 ? "bg-lime" : ""}`}
            >
              <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.25em] text-ink/65">
                <Icon size={11} strokeWidth={2.5} /> {s.label}
              </span>
              <p className="font-display text-4xl md:text-5xl leading-none mt-2 text-ink">
                {value == null ? "…" : value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Latest users */}
      <Section title="Latest signups" linkTo="/admin/users" linkLabel="See all users">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((u) => (
            <Link
              key={u.id}
              to={u.username ? `/u/${u.username}` : `/u/${u.id}`}
              className="pop-sm bg-paper p-4 flex items-center gap-3"
            >
              <Avatar src={u.avatar_url} name={u.full_name || u.display_name} size="md" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-ink truncate">
                  {u.full_name || u.display_name || "anon"}
                </p>
                <p className="text-ink/55 text-xs truncate">
                  @{u.username || "—"} · {fmt(u.created_at)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* Latest events */}
      <Section title="Latest events" linkTo="/admin/events" linkLabel="See all events">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((e) => (
            <Link
              key={e.id}
              to={`/events/${e.id}`}
              className="pop-sm bg-paper overflow-hidden flex"
            >
              <img src={coverFor(e)} alt="" className="w-24 h-24 object-cover border-r-[2.5px] border-ink shrink-0" loading="lazy" />
              <div className="flex-1 min-w-0 p-3">
                <p className="font-bold text-ink truncate">{e.title}</p>
                <p className="text-ink/55 text-xs truncate">
                  {e.category} · {e.city} · {fmt(e.created_at)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children, linkTo, linkLabel }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-3">
        <h2 className="font-display text-2xl md:text-3xl tracking-tight">{title}</h2>
        {linkTo && (
          <Link to={linkTo} className="text-[11px] font-extrabold uppercase tracking-widest text-ink/65 hover:text-ink">
            {linkLabel} →
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}
